import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useSelector } from "react-redux";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";

import { dispatch } from "../../../state/store";
import { setFWUVersions, setFWUVersionToDelete, setFWUVersionToEdit } from "../../../state/ducks";

import { routePaths, strCode } from "../../../consts";
import { useTableData } from "../../../hooks";
import { t } from "../../../services";

import { SmallButton, Table, TablePagination, WrapperContainer } from "../../../components";
import { FirmwaresTableColumns } from "./FirmwaresTableColumns";
import FilterTabContainer from "../Filters/FilterTabContainer";
import FirmwareVersionFiltersTab from "../Filters/FirmwareVersionFiltersTab";

const FirmwaresTable = ({
  firmwaresData = [],
  searchValue,
  setSearchValue,
  onCreateFwuVersion,
  onUpdateFWUVersion = () => {}
}) => {
  const firmwares = useSelector((store) => store.fwuVersions.fwuVersions);

  const loading = useSelector((state) => state.fwuVersions.loading);
  const fwuVersionToEdit = useSelector((state) => state.fwuVersions?.fwuVersionToEdit);

  const { deviceType, devices } = useSelector((store) => store.fleetManagement?.fwVersionFilters);

  const history = useHistory();

  const isRowForEditValid = useMemo(() => {
    if (fwuVersionToEdit) {
      return fwuVersionToEdit?.binFile?.name && fwuVersionToEdit?.signatureFile?.name;
    }
  }, [fwuVersionToEdit]);

  const onEditFinish = useCallback(
    (isConfirmed) => {
      if (isConfirmed) {
        if (fwuVersionToEdit && isRowForEditValid) {
          onUpdateFWUVersion(
            fwuVersionToEdit.fwuName,
            fwuVersionToEdit.binFile,
            fwuVersionToEdit.signatureFile
          );
        }
      } else {
        dispatch(setFWUVersionToEdit(null));
      }
    },
    [fwuVersionToEdit, isRowForEditValid]
  );

  const data = useTableData({ loading, data: firmwaresData, loadingPlaceholdersRows: 10 });

  const customSortTypes = {
    fwuIsNew: (rowA, rowB) => {
      const isNewA = rowA.original.isNew;
      const isNewB = rowB.original.isNew;

      if (isNewA === isNewB) {
        return Object.values(rowA.values.fwuVersion || {})
          .join(".")
          ?.localeCompare(Object.values(rowB.values.fwuVersion || {}).join("."));
      }

      return isNewA ? 1 : -1;
    }
  };

  const columns = useMemo(() => FirmwaresTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "fwuVersion",
            asc: true
          }
        ]
      },
      sortTypes: customSortTypes
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  useEffect(() => {
    setFilter("fwuName", searchValue ?? null);
    setFilter("model", { model: devices, type: deviceType });
  }, [searchValue, devices, deviceType]);

  const onEditFirmwareVersion = (fwuVersion) => {
    dispatch(setFWUVersionToEdit(fwuVersion));
    dispatch(setFWUVersions(firmwares.filter((firmware) => !firmware.isNew)));
  };

  const onDeleteFirmwareVersion = (fwuVersion) => {
    dispatch(setFWUVersionToDelete(fwuVersion));
  };

  const onEdit = (fwuVersion) => {
    history.push(`${routePaths.FW_VERSION_EDIT}/${fwuVersion.fwuName}`);
  };

  return (
    <WrapperContainer width="100%">
      <FilterTabContainer
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        searchPlaceholder={t(strCode.SEARCH_FOR_FIRMWARE)}
        ControlsComponent={
          <SmallButton onClick={onCreateFwuVersion} xShift={-64} disabled={loading}>
            {t(strCode.NEW_BTN)}
          </SmallButton>
        }
        FilterComponent={FirmwareVersionFiltersTab}
      />
      <Table
        tableKey="fw-table"
        tableInstance={tableInstance}
        callbackFn1={onEditFinish}
        callbackFn2={onDeleteFirmwareVersion}
        callbackFn4={onEditFirmwareVersion}
        onRowClick={onEdit}
        isLoading={loading}
        isValid={isRowForEditValid}
        searchedText={searchValue}
        tooltipLabel={t(strCode.EDIT)}
        hasPagination
      />
      <TablePagination
        tableKey="fw-table"
        tableSize={rows?.length}
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        onPrevPage={previousPage}
        onNextPage={nextPage}
        onFirstPage={() => gotoPage(0)}
        onLastPage={() => gotoPage(pageCount - 1)}
      />
    </WrapperContainer>
  );
};

FirmwaresTable.propTypes = {
  firmwaresData: PropTypes.arrayOf(
    PropTypes.shape({
      fwuVersion: PropTypes.string,
      deviceType: PropTypes.string,
      model: PropTypes.string,
      binFile: PropTypes.object,
      signatureFile: PropTypes.object,
      isNew: PropTypes.bool
    })
  ).isRequired,
  searchValue: PropTypes.string,
  deviceFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdateFWUVersion: PropTypes.func.isRequired,
  onCreateFwuVersion: PropTypes.func.isRequired,
  setSearchValue: PropTypes.func.isRequired
};

export default FirmwaresTable;
